// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

// --- Services ---
import stationSvc from "../../services/api/station";

// --- Models ---
import { StationItem, StationFilter } from "../../models/types/station";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import PopupCarousel from "../../components/carousel/popupCarousel";
import DialogComponent from "../../components/dialog";
import MenuItem from "@mui/material/MenuItem";
import ButtonMore from "../../components/button/buttonMore";

// --- Utils ---
import { MediaUrl } from "../../utils/configuration";
import { useResponsive } from "../../hooks/mediaQuery";

// --- Icons ---
import { Trash2, Pencil, Eye, NotebookPen, Download, FileIcon, ListMinus } from "lucide-react";
import StationFilterView from "./filter";
import { useTranslation } from "react-i18next";

export default function StationPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { isXs, isLg, isXl } = useResponsive();
    const { t } = useTranslation();

    const [data, setData] = useState<StationItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<StationItem | null>(null);
    const [openModalImage, setOpenModalImage] = useState<boolean>(false);
    const [openModalFile, setOpenModalFile] = useState<boolean>(false);

    const getListAll = async (params: DefaultFilterRequest<StationFilter>) => {
        setLoading(true);
        try {
            const res = await stationSvc.getAllData(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t("common.error"), t("common.errorMessage"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: StationItem) => {
        try {
            const res = await stationSvc.deleteStation(row.id.toString());
            if (res && res.content.status) {
                notify(t("common.success"), t("station.notifications.deleteSuccess"), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t("common.error"), t("common.errorMessage"), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<StationFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name"),
            address: queryParams.get("address"),
            type: queryParams.get("type"),
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleView = (row: StationItem) => {
        navigate(UrlCollection.ViewStation.replace(":id", row.id.toString()));
    };
    const handleViewSamplingSchedule = (row: StationItem) => {
        navigate(UrlCollection.SamplingSchedule.replace(":stationId", row.id.toString()));
    };
    const handleEdit = (row: StationItem) => {
        navigate(UrlCollection.EditStation.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: StationItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const handleViewImage = (row: StationItem) => {
        if (row.images && row.images.length > 0) {
            setOpenModalImage(true);
            setSelectedRow(row);
        } else {
            notify(t("common.warning"), t("station.notifications.noImages"), "warning");
        }
    };

    const handleViewFile = (row: StationItem) => {
        if (row.attachments && row.attachments.length > 0) {
            setOpenModalFile(true);
            setSelectedRow(row);
        } else {
            notify(t("common.warning"), t("station.notifications.noFiles"), "warning");
        }
    };

    const columns = [
        { header: t("station.table.stt"), accessor: "index", className: "w-[50px]" },
        { header: t("station.table.name"), accessor: "name", className: "min-w-[150px]" },
        { header: t("station.table.address"), accessor: "address", className: "min-w-[150px]" },
        { header: t("station.table.type"), accessor: "type" },

        {
            header: t("station.table.emailWarning"),
            accessor: (row: StationItem) => (
                row.emailWarning.join(", ")
            )
        },
        {
            header: t("station.table.smsWarning"),
            accessor: (row: StationItem) => (
                row.smsWarning.join(", ")
            )
        },
        {
            header: t("station.table.images"),
            accessor: (row: StationItem) => (
                <div onClick={() => handleViewImage(row)} className="cursor-pointer text-blue-500 hover:underline">
                    {t("station.actions.view")}
                </div>
            )
        },
        {
            header: t("station.table.files"),
            accessor: (row: StationItem) => (
                <div onClick={() => handleViewFile(row)} className="cursor-pointer text-blue-500 hover:underline">
                    {t("station.actions.view")}
                </div>
            )
        },
        {
            header: "",
            accessor: (row: StationItem) => {
                if (isXl || isLg) {
                    return (
                        <div className="flex gap-2">
                            <ButtonAction
                                onClick={() => notify("Hệ thống", "Đang cập nhật", "warning")}
                                icon={<Download size={14} />}
                                color="orange"
                                title={t("station.actions.sampling")}
                            />
                            <ButtonAction
                                onClick={() => handleViewSamplingSchedule(row)}
                                icon={<NotebookPen size={14} />}
                                color="blue"
                                title={t("station.actions.samplingSchedule")}
                            />
                            <ButtonAction
                                onClick={() => handleView(row)}
                                icon={<Eye size={14} />}
                                color="blue"
                                title={t("station.actions.view")}
                            />
                            <ButtonAction
                                onClick={() => handleEdit(row)}
                                icon={<Pencil size={14} />}
                                color="blue"
                                title={t("station.actions.edit")}
                            />
                            <ButtonAction
                                onClick={() => handleDelete(row)}
                                icon={<Trash2 size={14} />}
                                color="red"
                                title={t("station.actions.delete")}
                            />
                        </div>
                    )
                }
                else {
                    return (
                        <ButtonMore
                            onClick={() => handleView(row)}
                            icon={<ListMinus size={14} />}
                            title="More"
                        >
                            <MenuItem onClick={() => notify("Hệ thống", "Đang cập nhật", "warning")}>
                                <Download size={14} color="blue" className="mr-2" />
                                <span>{t("station.actions.sampling")}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleViewSamplingSchedule(row)}>
                                <NotebookPen size={14} color="blue" className="mr-2" />
                                <span>{t("station.actions.samplingSchedule")}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleView(row)}>
                                <Eye size={14} color="blue" className="mr-2" />
                                <span>{t("station.actions.view")}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleEdit(row)}>
                                <Pencil size={14} color="blue" className="mr-2" />
                                <span>{t("station.actions.edit")}</span>
                            </MenuItem>
                            <MenuItem onClick={() => handleDelete(row)}>
                                <Trash2 size={14} color="red" className="mr-2" />
                                <span>{t("station.actions.delete")}</span>
                            </MenuItem>
                        </ButtonMore>
                    )
                }
            },
            className: (!isXl && !isLg) ? "max-w-[50px]" : "",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header bg-gray-100">
                <h1 className="text-xl font-semibold">{t("station.title")}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateStation)}
                >
                    {t("station.addNew")}
                </button>
            </div>
            <div className={`flex ${isXs ? 'my-1' : 'my-2'}`}>
                <StationFilterView />
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t('station.modal.confirmDelete.title')}
                    content={`${t('station.modal.confirmDelete.content')} ${selectedRow?.name} ${t('station.modal.confirmDelete.contentEnd')}`}
                    titleComfirm={t('station.modal.confirmDelete.confirm')}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
            {openModalImage && (
                <PopupCarousel
                    files={selectedRow?.images || []}
                    isOpen={openModalImage}
                    onClose={() => setOpenModalImage(false)}
                    initIndex={0}
                />
            )}
            {openModalFile && (
                <DialogComponent isOpen={openModalFile} onClose={() => setOpenModalFile(false)} title={`Files đính kèm trạm ${selectedRow?.name}`}>
                    <div className="flex flex-col gap-2 pt-2">
                        {selectedRow?.attachments && selectedRow?.attachments?.length > 0 ? selectedRow.attachments.map((attachment, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <a href={MediaUrl + attachment.path} target="_blank" rel="noopener noreferrer" className="bg-gray-100 flex justify-center items-center gap-2 p-2">
                                    <FileIcon size={16} />
                                    <p>{attachment.fileName}</p>
                                </a>
                            </div>
                        )) : (
                            <p className="text-center">Không có tài liệu</p>
                        )}
                    </div>
                </DialogComponent>
            )}
        </div>
    );
}
